import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import { captureAttribution } from "@/lib/utm";
import RouteSeo from "@/seo/RouteSeo";
import Layout from "@/pages/Layout";
import Landing from "@/pages/Landing";
import SubPage from "@/pages/SubPage";
import PricingPage from "@/pages/PricingPage";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import DemoPage from "@/pages/DemoPage";
import PbxAnalyticsPage from "@/pages/PbxAnalyticsPage";
import SignupPage from "@/pages/SignupPage";
import LegalPage from "@/pages/LegalPage";
import DocsPage from "@/pages/DocsPage";
import GuidesPage from "@/pages/GuidesPage";
import AgentDemosPage from "@/pages/AgentDemosPage";
import NumbersPage from "@/pages/NumbersPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ComparePage, { CompareIndexPage } from "@/pages/ComparePage";
import { compareSlugs } from "@/data/compare";

// /docs/:slug — unknown slugs are redirected to /docs by DocsPage.
function DocsRoute() {
  const { slug } = useParams();
  return <DocsPage key={slug} slug={slug} />;
}

// /guides/:slug — unknown slugs are redirected to /guides by GuidesPage.
function GuideRoute() {
  const { slug } = useParams();
  return <GuidesPage key={slug} slug={slug} />;
}

const subRoutes = [
  "ai/overview", "ai/inbound", "ai/outbound", "ai/web-chat", "ai/multilingual", "ai/analytics",
  "pbx/multi-tenant", "pbx/features", "pbx/softphone", "pbx/security",
  "partners/reseller", "partners/white-label", "partners/affiliate",
];

// An ad links to /ai/demos but the form is on /demo, so the campaign has to
// survive the navigation in between — it is read on every route change and
// kept for the form to send.
function AttributionCapture() {
  const { search } = useLocation();
  useEffect(() => { captureAttribution(search); }, [search]);
  return null;
}

function App() {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="App">
        <BrowserRouter>
          <AttributionCapture />
          <RouteSeo />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/pbx/analytics" element={<PbxAnalyticsPage />} />
              <Route path="/ai/demos" element={<AgentDemosPage />} />
              <Route path="/numbers" element={<NumbersPage />} />
              <Route path="/privacy" element={<LegalPage slug="privacy" />} />
              <Route path="/terms" element={<LegalPage slug="terms" />} />
              <Route path="/refund-policy" element={<LegalPage slug="refund" />} />
              <Route path="/service-delivery" element={<LegalPage slug="delivery" />} />
              <Route path="/contact" element={<LegalPage slug="contact" />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/:slug" element={<DocsRoute />} />
              {/* Guides — the informational layer. A guide added to
                  src/content/guides.js is routed by this pair, but still needs
                  its title and description in src/seo/meta.js. */}
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/:slug" element={<GuideRoute />} />
              {subRoutes.map((s) => (
                <Route key={s} path={`/${s}`} element={<SubPage slug={s} />} />
              ))}
              {/* Comparison pages — the slugs come from the data file so a page
                  added there is routed without touching this list. Its meta
                  tags still have to be added to src/seo/meta.js. */}
              <Route path="/compare" element={<CompareIndexPage />} />
              {compareSlugs.map((s) => (
                <Route key={s} path={`/${s}`} element={<ComparePage slug={s} />} />
              ))}
              {/* Any other address. netlify.toml answers these with
                  build/404.html and a 404 status, so the page and the status
                  code say the same thing. */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" theme="light" />
      </div>
    </ReactLenis>
  );
}

export default App;
